import { Link } from "wouter";
import { DEFAULT_RETENTION_DAYS } from "@titleverify/shared";
import { useMe } from "../me.js";
import { SUPPORT_EMAIL } from "../support.js";

/**
 * WHAT THIS PLATFORM HOLDS ABOUT YOU. EVERY LINE VERIFIED AGAINST THE CODE.
 * ===========================================================================
 *
 * This page is the one place a signed-in person can find out what is stored,
 * who can reach it, how long it stays, and what they can withdraw. It is
 * therefore the single easiest page in the product to make dishonest, because
 * every reassuring sentence one could write here is a sentence a reader would be
 * glad to believe.
 *
 * THE RULE FOR EDITING IT: no claim goes on this page that is not true of the
 * code in this repository TODAY. Not true of the design, not true of the
 * schema's intention, not true once MD6 ships — true now. Where the intention
 * and the implementation differ, the implementation is what gets described and
 * the gap is stated. Three places below do exactly that, and they are the most
 * valuable paragraphs here:
 *
 *   1. The retention clock is recorded but NOTHING DELETES. `retention_expires_at`
 *      is real and NOT NULL, and there is no retention job anywhere in the API —
 *      `RETENTION_EXPIRED` appears only in tests and in the privilege allowlist.
 *      Saying "deleted after 90 days" would be a promise nothing keeps.
 *   2. Meeting addresses and report free text have NO clock at all, which the
 *      schema says out loud at TODO-BLOCKED-MEETING-RETENTION
 *      (apps/api/src/db/schema/meetings.ts) and TODO-BLOCKED-REPORT-RETENTION
 *      (apps/api/src/db/schema/client-reports.ts).
 *   3. Erasure exists as a data model and NOT as a route. No endpoint stamps
 *      `erased_at`; the only code that reads it is the middleware that refuses an
 *      already-erased principal. So the page says to ask a person, rather than
 *      pointing at a self-service button that is not there.
 *
 * WHAT IS DELIBERATELY ABSENT: any security badge, any "bank-level" anything,
 * any percentage, any count of users or cases, any seal. A padlock icon is worth
 * nothing and implies an audit nobody performed. What is here instead are
 * specific, checkable mechanisms — a 300-second link, a database CHECK
 * constraint, a permission that does not exist for admins — because a reader who
 * doubts one of them can be shown the line of code.
 */
export function DataHandling() {
  const me = useMe();

  return (
    <section>
      <p className="breadcrumb">
        <Link href="/">← Your account</Link>
      </p>

      <header className="page-head">
        <p className="eyebrow">Your data</p>
        <h2>What TitleVerify holds about you</h2>
        <p className="lede">
          Land and identity documents are sensitive, and a page like this is usually written to
          reassure. This one is written to be accurate. Where the platform does something well it
          says what the mechanism is, and where it falls short of what it intends it says that
          too, in the same plain terms.
        </p>
      </header>

      <h3>What is stored</h3>
      <dl>
        <dt>Your account</dt>
        <dd>
          An opaque identifier from the sign-in provider, and your role on this platform. That
          is genuinely all — see the next section.
        </dd>

        <dt>What you told us about the parcel</dt>
        <dd>
          The district, Divisional Secretariat division, village, address or directions, survey
          plan and lot number, extent, deed number, date and attesting notary, and any earlier
          deed references you listed. This is what you typed into the request form, held as you
          gave it.
        </dd>

        <dt>Documents you upload</dt>
        <dd>
          The file itself, as a private object; its SHA-256 digest, computed by the server from
          the bytes it stored rather than accepted from your browser; its size and file type; and
          the date it must be held until.{" "}
          <strong>The filename is not sent, not stored and not logged.</strong> Filenames
          routinely carry a person&rsquo;s name, and dropping it at the door is cheaper than
          erasing it from storage keys and logs later.
        </dd>

        <dt>Meetings, if any are arranged on your case</dt>
        <dd>
          The mode, the proposed time, and — depending on the mode — a location or a joining
          link. If you type a physical address there, that address is what is stored.
        </dd>

        <dt>Anything you report to us</dt>
        <dd>
          The category you chose and, if you wrote one, your account of what happened, in your
          own words. It is recorded as <em>your</em> account and the platform does not adopt it.
        </dd>

        <dt>A record of what was done</dt>
        <dd>
          Who did what to which record, and when. This log is append-only — nothing on this
          platform can edit or delete an entry, including staff — and because it cannot be
          erased, it is meant to hold nothing that would ever need erasing: identifiers, fixed
          codes and dates only.
          {/*
            The check in audit/metadata.ts describes ITSELF as "a tripwire, not a
            proof" — it catches an email or a phone number dropped into a log
            entry by mistake and explicitly "cannot catch a determined caller who
            stuffs a name into { code: ... }". Describing it as a guarantee on a
            page whose entire premise is not over-claiming would be the page
            failing its own test. So the limit is stated.
          */}{" "}
          A database rule blocks the obvious mistakes — an entry carrying something shaped like
          an email address, a telephone number or a national identity card number is refused
          outright. It is a tripwire rather than a proof: it catches the errors people actually
          make, and it could not stop someone determined to disguise personal data as a code.
        </dd>
      </dl>

      {/*
        The strongest honest statement available, and it is stronger than the
        invented ones. Verified: apps/web/src/App.tsx posts only { accountType }
        to /accounts, so users.email, users.phone and users.full_name are null
        for every account created through this app, and no route writes them
        afterwards — nothing in the API calls db.update(users) for those columns
        at all.
      */}
      <div className="notice">
        <h3>TitleVerify does not hold your name, email address or telephone number</h3>
        <p>
          Not as a policy — the columns exist and are empty, because nothing in this application
          ever fills them. When you created your account, the only thing sent to TitleVerify was
          which kind of account you wanted. There is no screen anywhere here for a name, a phone
          number or a postal address, and there is no request this application makes that would
          store one.
        </p>
        <p>
          <strong>Your email address is held by the sign-in provider, not by us.</strong> Signing
          in goes through Google&rsquo;s Firebase Authentication, which is where your email and
          password live. That service processes identity data in the United States and offers no
          region setting, so your email address sits there while your documents sit in the Mumbai
          region. Two different countries, and worth knowing rather than discovering.
        </p>
        <p>
          One consequence is worth being blunt about: if we need to telephone you — about a
          problem you report, for instance — <strong>we have no number to call</strong>. That is
          a real gap in the service, not a privacy feature we are dressing up.
        </p>
      </div>

      <h3>Who can see it</h3>
      <dl>
        <dt>You</dt>
        <dd>Your own requests and every document held on them.</dd>

        <dt>The attorney assigned to your case</dt>
        <dd>
          {/*
            SAYS WHAT THE CODE GATES ON, not what we would like it to gate on —
            and this note is now the record of a claim that had to be made
            STRONGER, which is the direction this file has never had to move in
            before.

            The history matters, because the temptation is to delete it. This
            once read "Only an attorney-at-law … can be assigned at all", which
            was false: the override path checked verification and nothing else.
            The comment was then corrected to say so, and the copy was weakened to
            "a verified professional" and an explicit disclaimer that the platform
            "does not separately require" an attorney. Both halves of that are now
            WRONG IN THE OTHER DIRECTION, and understating a safeguard that exists
            is as dishonest as overstating one that does not:

              - migration 0025 §2's `assignee_professional_is_an_attorney` refuses
                any assignee whose `professional_profiles.type` is not attorney or
                both, on EVERY path including the override, at the database.
              - migration 0026 §1's `assignment_is_within_the_panel` now binds
                every selection_method, so the assignee must also hold a live,
                unpaused seat for the registry the parcel is in. The override was
                exempt from that until 0026.
              - migration 0026 §2 refuses an assignee whose account has been
                erased, and erasure ends their seats and assignments outright.

            So the sentence below states the attorney rule and the jurisdiction
            rule as facts, because they are enforced at the database on every
            path, not in one handler that a future one could forget.
          */}
          That case and every document on it, from the moment the assignment is made. Only an
          attorney-at-law can hold your case, and only one registered for the land registry your
          parcel sits in — checked when the assignment is written, whichever way it was made.
          Nobody is assignable until an administrator has checked their standing against a source
          they had to name and record. If an assignment is revoked, the access goes with it.
        </dd>

        <dt>A search agent, if one is used on your case</dt>
        <dd>
          Only the extracts retrieved at the registry — <strong>never</strong> what you uploaded.
          A search agent does registry legwork and has no need to see your identity papers, so
          the permission is written to exclude them rather than to trust the person to look away.
        </dd>

        <dt>TitleVerify&rsquo;s own staff</dt>
        <dd>
          Case details — status, jurisdiction, dates — and <strong>not your documents</strong>.
          There is no administrator permission to read a case document on this platform. It is
          not a setting that is switched off; the rule does not exist, so ops screens receive an
          empty list for every case in the system.
        </dd>
      </dl>

      <div className="caveat">
        <p>
          <strong>Two things that follow from that, stated rather than implied.</strong> Because
          staff cannot read your documents, they also cannot check one for you, recover one you
          can no longer open, or tell you what a document contains. And because access is decided
          per case, a person who could see your case yesterday may not be able to today — the
          record of who was given access, and when, is in the append-only log described above.
        </p>
      </div>

      <h3>How documents are released</h3>
      <p>
        A document is never a public file and there is no lasting link to one. Each time you open
        a document, the server mints a fresh link that stops working{" "}
        <strong>five minutes</strong> after it is issued, hands it to your browser marked not to
        be cached, and records that it issued one.
      </p>
      {/*
        THIS PARAGRAPH REPLACED A FALSE ONE. The page said the link was
        "single-use". It is not, and never was: storage/gcs.ts signedReadUrl
        issues a GCS V4 signed read URL whose only limit is the expiry
        (SIGNED_URL_TTL_SECONDS = 300 in storage/store.ts). A V4 signature has no
        use counter and no binding to the person who asked — it is a bearer
        capability, replayable by anyone holding the string until it expires.
        "Single-use" described a stronger safeguard than the code implements,
        which is the one thing this page exists not to do. The limit is now
        stated instead, because a reader who thinks the link is spent on first
        use will forward it.
      */}
      <div className="caveat">
        <p>
          <strong>For those five minutes the link is a key, not a door with your name on it.</strong>{" "}
          Anyone holding it can open that document with it, as many times as they like, without
          signing in — it is not tied to you and it is not used up by being opened once. That is
          why it is short-lived, never stored, and never to be forwarded. After five minutes it
          stops working and the next look needs a new one.
        </p>
      </div>
      <p>
        The log entry says a link was <em>issued</em>, not that the document was{" "}
        <em>viewed</em>. The platform knows it handed out a link. It does not know whether anyone
        opened it, and recording the second would be stating something it did not observe.
      </p>
      <p>
        There is no code path in this application that can make a stored document public. The
        storage layer has no publish operation, and the database refuses to store a document
        address that looks like a web link at all.
      </p>

      <h3>How long it is kept</h3>
      {/*
        THE MOST IMPORTANT PANEL ON THE PAGE, and the one a normal product would
        get wrong in the reassuring direction. The date is real; the deletion is
        not automated. Both halves are said, and the second is not buried.
      */}
      <div className="caveat">
        <p>
          <strong>Every document is given a deletion date when it is uploaded</strong> — by
          default {DEFAULT_RETENTION_DAYS} days from the moment we take custody, not from when
          your case finishes. The date is mandatory: the database will not accept a document
          without one, by any route, so there is no document here without a clock on it. You can
          see the date against each document on your request.
        </p>
        <p>
          <strong>
            But nothing deletes them automatically yet. The job that would do it has not been
            built.
          </strong>{" "}
          So the date on a document is the date it becomes <em>due</em> for deletion, and not a
          record that anything has happened to it. A document is shown as deleted only when the
          service reports it deleted. We would rather tell you this than let a date on a screen
          imply a guarantee nothing currently keeps.
        </p>
        <p>
          Two other kinds of data have <strong>no deletion clock at all</strong>, which the code
          states openly rather than leaves to be discovered: an address or joining link recorded
          for a meeting, and the words you write when you report a problem. If you would rather
          not leave a home address on this platform indefinitely, enter only what is needed to
          find the place.
        </p>
        <p>
          The record of a request, and the append-only log of what was done to it, are kept after
          a document is gone. That is deliberate — it is how it stays possible to show that a
          document was held and deleted on schedule.
        </p>
      </div>

      <h3>What you can withdraw</h3>
      <p>
        When you open a request, you choose whether TitleVerify may contact you by email or by
        WhatsApp. Neither is ticked for you. Each is recorded separately, per request, and each
        can be withdrawn — on the request itself, or across every request at once. Withdrawing
        cannot be undone.
      </p>
      {/*
        THE SAME FALSE DELIVERY CLAIM AS THE CONSENT FIELDSET, second copy.
        "it is where the report is delivered" — nothing delivers a report. There
        is no attorney-opinion entity and no route that hands one to a client;
        `apps/api/src/reports` is the client problem-report module and is a
        different thing. The clause was already deleted from the landing page for
        this reason and had to be chased to both remaining copies.

        What is true, and is what the sentence should have said all along: the
        in_app consent row is written unconditionally by `recordConsents` and is
        not offered as a choice. That is a fact about the consent record, which
        is what this section is about.
      */}
      <p>
        Being told inside this app is not offered as a choice: this is the primary place the
        platform is built to reach you, so the permission is recorded with every request and
        there is no control to switch it off. In practice the app itself raises nothing at
        you — your requests and their progress are on your own page whenever you open it.
      </p>
      {/*
        The claim a consent screen is most tempted to make, and the one that would
        be false. Verified: hasConsentForCase is exported for its tests and no
        route calls it, and there is no mailer or WhatsApp client anywhere in
        apps/api/src. Nothing is sent on either channel today.
      */}
      <div className="notice">
        <p>
          <strong>One message exists: an email saying an attorney's opinion on a request is
          ready.</strong> It carries a link back to this app and nothing about your land.
          Withdrawing email changes the permission on record — which is what a send is checked
          against — and so it does stop that message. WhatsApp remains unused: no part of this
          platform can send on it, and there is nowhere here to give us a number.
        </p>
      </div>
      {me.data?.role === "client" ? (
        <p>
          <Link href="/">Your requests</Link> is where both controls are: on each request, and
          the account-wide one at the foot of the page.
        </p>
      ) : null}

      <h3>Correcting or erasing what is held</h3>
      {/*
        THIS SECTION CARRIED THE PAGE'S WORST CLAIM, in the place a reader is
        least able to afford it: "what you have already given is never
        overwritten by doing so", under the heading about correcting a record.

        The server does not uphold it. `supplementIntake` (intake/service.ts)
        merges `patch.X ?? parcel.X` and then UPDATEs `parcels` in place. Any
        field present in the patch replaces what was stored — the function's own
        audit entry emits `change: "replaced"`, and its own doc comment says
        "once a district is overwritten the old one is gone". There is no
        parcel-revision table; the audit log deliberately records field NAMES
        and not values, so the superseded value is not recoverable from it
        either.

        The only thing that made the sentence look true was THE BROWSER:
        SupplementCase renders fields for the outstanding gaps and posts only
        keys the client typed into. That is a real property of the shipped
        client UI and worth telling a client — but a browser is not a guarantee,
        and this page's own stated standard (see the header) is that a claim
        must be true of the CODE. A client with the endpoint could replace their
        own stored values today.

        Worse, `case:update` is granted to admin (every case), and to an
        assigned professional and search agent (authorize.ts) — so a staff
        account can rewrite the deed number, lot, extent and the district that
        drives registry routing, unrecoverably. That is not a footnote on a page
        about who can reach your data; it is the answer to the question the
        heading asks.

        So the "never" is gone and what replaces it is the actual position:
        which route can change what, whether the old value survives, and what
        the log does and does not preserve. The `case:update` grant and the
        missing revision table are both server matters and are not fixed from
        here — stating them is what this page can honestly do about them.
      */}
      <p>
        You can add to a request at any time it is waiting on you. This application only ever
        shows you the details that are still outstanding, and it sends only the boxes you
        actually type into — so through the screens here, adding to a request cannot overwrite
        something you gave us earlier.
      </p>
      <div className="caveat">
        <p>
          <strong>
            That is a property of these screens, not a lock on the record underneath.
          </strong>{" "}
          The service accepts a correction to any parcel detail while a request is still
          waiting on you or has not been sent, and a corrected value <em>replaces</em> the old
          one rather than sitting alongside it. There is no history of superseded values: once
          a detail is changed, the previous one is gone. We would rather say that than let
          &ldquo;you can only add&rdquo; stand as a promise about the record.
        </p>
        <p>
          <strong>And it is not only you who can change it.</strong> A TitleVerify
          administrator can, as can the attorney assigned to your case and any search agent
          working on it. The record of that is real and is the append-only log described
          above: it captures who made the change, when, and <em>which</em> details they
          changed — the district, the lot, the deed number. What it does not capture is what
          the value was before, because that log can never be erased and a deed number written
          into it would outlive any request you made to erase it. So the log can always answer
          who changed what and when. It cannot restore what was there.
        </p>
      </div>
      <div className="caveat">
        <p>
          <strong>Erasure is not something you can do from this screen, and there is no button
          for it anywhere in this application.</strong> The platform is built to erase an account
          by emptying its personal fields while keeping an opaque identifier, so the record of
          what was done stays intact without holding on to who you are — but the code that
          performs it has not been written.{" "}
          {SUPPORT_EMAIL ? (
            <>
              Ask a person at TitleVerify —{" "}
              <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> — and treat any answer
              that is not from a person as not yet real.
            </>
          ) : (
            /*
              No address is configured, so this does not send the reader looking
              for a contact route that does not exist. Erasure is a right under
              the PDPA; telling someone to "ask a person" while giving them no
              way to reach one is the failure this whole change is about. When
              VITE_SUPPORT_EMAIL is set the sentence above names the address.
            */
            <>
              There is no way to ask us from inside this application yet, and no address
              published on it. Until there is, this right is one the platform cannot
              actually let you exercise — which is stated here rather than left for you
              to discover.
            </>
          )}
        </p>
      </div>

      <div className="consequence">
        <h3>If you think any of this is wrong</h3>
        <p>
          This page describes the platform as it is actually built, and it was written by
          checking each statement against the code rather than against the intention. If
          something here does not match what you have seen, that is worth telling us — either it
          is a mistake on this page or it is a mistake in the platform, and both are ours.
        </p>
      </div>
    </section>
  );
}
